import { z } from 'zod';

import { apiRequest } from '@/api/client';

/**
 * Languages and a store's language settings, mirroring docs/i18n.md §7.
 *
 * Languages are referred to by `code` everywhere; ids are carried for
 * completeness only.
 */

export const LANGUAGE_DIRECTIONS = ['LTR', 'RTL'] as const;

export type LanguageDirection = (typeof LANGUAGE_DIRECTIONS)[number];

/** Error codes the settings endpoints answer with. Dictionary keys: `languages.errors.<code>`. */
export const LANGUAGE_SETTINGS_ERRORS = [
  'NO_LANGUAGES_ENABLED',
  'DEFAULT_LANGUAGE_NOT_ENABLED',
  'LANGUAGE_NOT_AVAILABLE',
  'PLAN_LIMIT_REACHED',
  'LANGUAGE_SETTINGS_CONFLICT',
] as const;

export const languageSchema = z.object({
  id: z.string(),
  code: z.string(),
  locale: z.string(),
  name: z.string(),
  native_name: z.string(),
  direction: z.enum(LANGUAGE_DIRECTIONS),
  is_active: z.boolean(),
  is_system_language: z.boolean(),
  position: z.number(),
});

export type Language = z.infer<typeof languageSchema>;

/** One enabled language in a store, in switcher order. */
export const storeLanguageSchema = z.object({
  code: z.string(),
  locale: z.string(),
  name: z.string(),
  native_name: z.string(),
  direction: z.enum(LANGUAGE_DIRECTIONS),
  position: z.number(),
  is_default: z.boolean(),
});

export type StoreLanguage = z.infer<typeof storeLanguageSchema>;

export const storeLanguageSettingsSchema = z.object({
  languages: z.array(storeLanguageSchema),
  default_language: z.string(),
  admin_language: z.string().nullable(),
  fallback_to_default: z.boolean(),
  detect_browser_language: z.boolean(),
  switcher_enabled: z.boolean(),
  /** The plan's `MAX_LANGUAGES`; null means unlimited. */
  limit: z.number().nullable(),
  /** Every active platform language a merchant could enable. */
  available: z.array(languageSchema),
});

export type StoreLanguageSettings = z.infer<typeof storeLanguageSettingsSchema>;

export interface StoreLanguageSettingsUpdate {
  /** Enabled codes, in switcher order. */
  enabled: string[];
  default_language: string;
  admin_language?: string | null;
  fallback_to_default?: boolean;
  detect_browser_language?: boolean;
  switcher_enabled?: boolean;
}

export const myLanguageSchema = z.object({
  preferred: z.string().nullable(),
  effective: z.string(),
  store_admin_language: z.string().nullable(),
});

export type MyLanguage = z.infer<typeof myLanguageSchema>;

export const languageKeys = {
  all: ['i18n', 'languages'] as const,
  store: ['i18n', 'store-languages'] as const,
  mine: ['i18n', 'my-language'] as const,
};

/** Active platform languages. */
export function fetchLanguages() {
  return apiRequest('/languages', z.array(languageSchema));
}

export function fetchStoreLanguageSettings() {
  return apiRequest('/stores/me/languages', storeLanguageSettingsSchema);
}

export function updateStoreLanguageSettings(payload: StoreLanguageSettingsUpdate) {
  return apiRequest('/stores/me/languages', storeLanguageSettingsSchema, {
    method: 'PUT',
    body: payload,
  });
}

/** The signed-in merchant's dashboard language. */
export function fetchMyLanguage() {
  return apiRequest('/i18n/me/language', myLanguageSchema);
}

export function updateMyLanguage(preferred: string | null) {
  return apiRequest('/i18n/me/language', myLanguageSchema, {
    method: 'PUT',
    body: { preferred },
  });
}
